// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  type CognitoOAuthConfig,
  type AuthState,
  exchangeCodeForTokens,
  refreshAccessToken,
  buildLoginRedirectUrl,
  validateOAuthState,
} from "./cognitoOAuth";
import { getLocationSearch, getLocationPathname, navigateTo } from "./locationUtils";

export { type CognitoOAuthConfig } from "./cognitoOAuth";

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

export interface AuthContextType {
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  loading: boolean;
  accessToken: string | null;
  error: string | null;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface CognitoAuthProviderProps {
  oauthConfig: CognitoOAuthConfig;
  children: React.ReactNode;
}

/** Buffer (ms) before actual expiry to trigger a proactive refresh. */
const REFRESH_BUFFER_MS = 60_000;

export function CognitoAuthProvider({
  oauthConfig,
  children,
}: CognitoAuthProviderProps): React.ReactElement {
  const [authState, setAuthState] = useState<AuthState>({
    accessToken: null,
    idToken: null,
    refreshToken: null,
    expiresAt: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initRef = useRef(false);

  // ── Helpers ──────────────────────────────────────────────────────────

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const redirectToLanding = useCallback(() => {
    navigateTo("/");
  }, []);

  const redirectToLogin = useCallback(() => {
    void buildLoginRedirectUrl(oauthConfig).then((url) => navigateTo(url));
  }, [oauthConfig]);

  const handleRefresh = useCallback(
    async (refreshToken: string) => {
      try {
        const newState = await refreshAccessToken(refreshToken, oauthConfig);
        setAuthState(newState);
      } catch {
        // Refresh failed → clear state and send to landing page
        setAuthState({
          accessToken: null,
          idToken: null,
          refreshToken: null,
          expiresAt: null,
        });
        setError("Session expired. Please sign in again.");
        redirectToLanding();
      }
    },
    [oauthConfig, redirectToLanding],
  );

  // ── Schedule proactive token refresh ────────────────────────────────

  useEffect(() => {
    clearRefreshTimer();

    if (authState.accessToken && authState.refreshToken && authState.expiresAt) {
      const delay = Math.max(authState.expiresAt - Date.now() - REFRESH_BUFFER_MS, 0);
      refreshTimerRef.current = setTimeout(() => {
        void handleRefresh(authState.refreshToken!);
      }, delay);
    }

    return clearRefreshTimer;
  }, [authState, handleRefresh, clearRefreshTimer]);

  // ── Initialisation: code exchange or redirect ───────────────────────

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const params = new URLSearchParams(getLocationSearch());
    const code = params.get("code");
    const state = params.get("state");

    if (code) {
      // Validate OAuth state parameter (CSRF protection)
      if (!validateOAuthState(state)) {
        setError("Invalid OAuth state. Possible CSRF attack. Please sign in again.");
        setLoading(false);
        redirectToLanding();
        return;
      }

      // Strip the code and state from the URL so a refresh doesn't replay it
      window.history.replaceState({}, document.title, getLocationPathname());

      exchangeCodeForTokens(code, oauthConfig)
        .then((state) => {
          setAuthState(state);
          setLoading(false);
        })
        .catch(() => {
          setError("Token exchange failed. Please sign in again.");
          setLoading(false);
          redirectToLanding();
        });
    } else if (!authState.accessToken) {
      // No tokens and no code → show landing page (user clicks Login to redirect)
      setLoading(false);
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Logout ──────────────────────────────────────────────────────────

  const logout = useCallback(() => {
    clearRefreshTimer();
    setAuthState({
      accessToken: null,
      idToken: null,
      refreshToken: null,
      expiresAt: null,
    });
    setError(null);
    // Redirect to Cognito logout endpoint (Req 5.11)
    navigateTo(oauthConfig.logoutUrl);
  }, [oauthConfig, clearRefreshTimer]);

  // ── Build tokens object for backward-compat consumers ───────────────

  const tokens: AuthTokens | null =
    authState.accessToken && authState.idToken && authState.refreshToken
      ? {
          accessToken: authState.accessToken,
          idToken: authState.idToken,
          refreshToken: authState.refreshToken,
        }
      : null;

  const value: AuthContextType = {
    tokens,
    isAuthenticated: tokens !== null,
    loading,
    accessToken: authState.accessToken,
    error,
    login: redirectToLogin,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within a CognitoAuthProvider");
  }
  return context;
}
